import { removeUndefinedValues } from "../utils/firestore";
import { CollectionPaths } from "@order-up/common";
import { BusinessSubCategory, IBusinessSubCategoryData } from "@order-up/models";
import {
  DocumentWithId,
  FirestoreRepository,
} from "../interfaces/FirestoreRepository";
import { createLogger } from "../utils/logger";
import { counterService } from "./counters";

const logger = createLogger("BusinessSubCategoryService");

const businessSubCategoriesPath = () => CollectionPaths.businessSubCategories();

/**
 * Platform-level collection: access via Firebase client SDK only.
 * Admin app writes, management/admin apps read.
 */

const getBusinessSubCategories = async (): Promise<
  DocumentWithId<BusinessSubCategory>[]
> => {
  logger.debug("Getting business sub-categories");

  try {
    const collectionPath = businessSubCategoriesPath();
    const repo = new FirestoreRepository<BusinessSubCategory>(
      collectionPath,
      BusinessSubCategory,
    );
    const subCategories = await repo.find();
    return subCategories;
  } catch (error) {
    logger.error("Error fetching business sub-categories", error);
    throw error;
  }
};

const getBusinessSubCategoriesByType = async (
  businessTypeGuid: string,
): Promise<DocumentWithId<BusinessSubCategory>[]> => {
  logger.debug("Getting business sub-categories by type", { businessTypeGuid });

  try {
    const collectionPath = businessSubCategoriesPath();
    const repo = new FirestoreRepository<BusinessSubCategory>(
      collectionPath,
      BusinessSubCategory,
    );
    const subCategories = await repo.find([
      {
        field: "businessTypeGuid",
        operator: "==",
        value: businessTypeGuid,
      },
    ]);
    return subCategories;
  } catch (error) {
    logger.error("Error fetching business sub-categories by type", error);
    throw error;
  }
};

const createBusinessSubCategory = async (
  data: Omit<
    IBusinessSubCategoryData,
    "businessSubCategoryId" | "guid" | "createdAt" | "updatedAt"
  > & {
    businessTypeGuid: string;
    name: string;
  },
): Promise<DocumentWithId<BusinessSubCategory>> => {
  logger.debug("Creating business sub-category", { name: data.name });

  const { businessSubCategoryId, guid } =
    await counterService.generateBusinessSubCategoryId();

  const payload = removeUndefinedValues({
    businessSubCategoryId,
    guid,
    businessTypeGuid: data.businessTypeGuid,
    name: data.name,
    isActive: data.isActive ?? true,
    position: data.position ?? 0,
    createdAt: FirestoreRepository.getServerTimestamp(),
    updatedAt: FirestoreRepository.getServerTimestamp(),
  });

  const collectionPath = businessSubCategoriesPath();
  const repo = new FirestoreRepository<BusinessSubCategory>(
    collectionPath,
    BusinessSubCategory,
  );
  const created = await repo.create(payload as any);
  const out = await repo.getById(created.docId);
  if (!out) throw new Error("Failed to retrieve created business sub-category");
  return out;
};

// Mutations resolve the target by `guid` (the model's stable, unique external
// reference) rather than `businessSubCategoryId`. The numeric counter ID is not
// guaranteed unique across counter resets/re-seeds, so a `businessSubCategoryId`
// lookup could mutate the wrong document; `guid` is a collision-proof UUID.
const updateBusinessSubCategory = async (
  guid: string,
  data: Partial<
    Omit<IBusinessSubCategoryData, "businessSubCategoryId" | "guid" | "createdAt">
  >,
): Promise<DocumentWithId<BusinessSubCategory>> => {
  logger.debug("Updating business sub-category", { guid });

  const collectionPath = businessSubCategoriesPath();
  const repo = new FirestoreRepository<BusinessSubCategory>(
    collectionPath,
    BusinessSubCategory,
  );
  const results = await repo.find([
    {
      field: "guid",
      operator: "==",
      value: guid,
    },
  ]);
  if (results.length === 0)
    throw new Error(`Business sub-category not found: ${guid}`);

  const existingDocId = results[0].docId;
  const updatePayload = removeUndefinedValues({
    ...data,
    updatedAt: FirestoreRepository.getServerTimestamp(),
  });

  await repo.updateById(existingDocId, updatePayload);
  const updated = await repo.getById(existingDocId);
  if (!updated)
    throw new Error(
      `Failed to retrieve updated business sub-category ${guid}`,
    );
  return updated;
};

const deleteBusinessSubCategory = async (
  guid: string,
): Promise<void> => {
  logger.debug("Deleting business sub-category", { guid });

  const collectionPath = businessSubCategoriesPath();
  const repo = new FirestoreRepository<BusinessSubCategory>(
    collectionPath,
    BusinessSubCategory,
  );
  const results = await repo.find([
    {
      field: "guid",
      operator: "==",
      value: guid,
    },
  ]);
  if (results.length === 0)
    throw new Error(`Business sub-category not found: ${guid}`);
  await repo.deleteById(results[0].docId);
};

const subscribeToBusinessSubCategories = (
  callback: (subCategories: DocumentWithId<BusinessSubCategory>[]) => void,
  onError?: (error: Error) => void,
): (() => void) => {
  const collectionPath = businessSubCategoriesPath();
  const repo = new FirestoreRepository<BusinessSubCategory>(
    collectionPath,
    BusinessSubCategory,
  );
  return repo.subscribeToCollection([], [], callback, onError);
};

export const businessSubCategoryService = {
  getBusinessSubCategories,
  getBusinessSubCategoriesByType,
  createBusinessSubCategory,
  updateBusinessSubCategory,
  deleteBusinessSubCategory,
  subscribeToBusinessSubCategories,
};
